/*
  Customily Modal Bridge
  ----------------------
  When the Customily app injects its customizer UI on a product page, this
  script:
    1. Detects the injected container (multiple known selectors + a fallback
       that looks for any element with `customily`/`cstmly` in its id or class).
    2. Hides that container in place.
    3. Hides the existing "Add to Cart" submit button(s).
    4. Hides the legacy locket Start Customize button if it's also on the page
       (Customily takes precedence on configured products).
    5. Renders a locket-styled "Start Customizing" trigger button.
    6. On click, moves Customily's container into a full-screen modal and
       reveals it; Customily continues driving its own internal step UI.
    7. On modal close, moves Customily's container back to its original
       location (so its state and listeners survive) and reveals the
       Add to Cart button so the customer can finalize the purchase.

  Designed to be safe on products that do NOT have Customily — if no
  Customily container is ever injected, the script does nothing.
*/

(function () {
  'use strict';

  if (window.__customilyModalBridgeInit) return;
  window.__customilyModalBridgeInit = true;

  const STATE = {
    container: null,
    originalParent: null,
    originalNextSibling: null,
    sentinel: null,
    locketBtn: null,
    triggerBtn: null,
    modal: null,
    modalBody: null,
    atcButtons: [],
    section: null,
  };

  const KNOWN_SELECTORS = [
    '#customily-product-options-container',
    '#customily-product-customizer',
    '#customily-options',
    '.customily-product-options-container',
    '.customily-customizer-container',
    '[data-customily-product-id]',
    '[data-cstmly-product-id]',
    '[data-customily-options]',
  ];

  function findCustomilyContainer() {
    for (const sel of KNOWN_SELECTORS) {
      const el = document.querySelector(sel);
      if (el && el.children && el.children.length > 0) return el;
    }
    // Fallback: any element with customily/cstmly in id or class that has children
    const candidates = Array.from(
      document.querySelectorAll('[id*="customily" i], [class*="customily" i], [id*="cstmly" i], [class*="cstmly" i]')
    ).filter((el) => {
      if (!el.children || el.children.length === 0) return false;
      // Skip our own bridge elements
      if (el.closest('.customily-modal')) return false;
      if (el.classList.contains('customily-start-customize-btn')) return false;
      return true;
    });
    if (!candidates.length) return null;
    candidates.sort((a, b) => b.children.length - a.children.length);
    return candidates[0];
  }

  function buildModal(sectionId) {
    const modal = document.createElement('div');
    modal.className = 'customily-modal';
    modal.id = 'customily-modal-' + sectionId;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="customily-modal__backdrop" tabindex="-1"></div>' +
      '<div class="customily-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="customily-modal-title-' + sectionId + '">' +
        '<div class="customily-modal__header">' +
          '<h2 class="customily-modal__title" id="customily-modal-title-' + sectionId + '">Customize</h2>' +
          '<button type="button" class="customily-modal__close" aria-label="Close customizer">&times;</button>' +
        '</div>' +
        '<div class="customily-modal__body"></div>' +
        '<div class="customily-modal__footer">' +
          '<button type="button" class="customily-modal__done button button--primary">DONE</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    return modal;
  }

  function init() {
    if (STATE.container) return true;
    const container = findCustomilyContainer();
    if (!container) return false;

    STATE.container = container;
    STATE.originalParent = container.parentNode;
    STATE.originalNextSibling = container.nextSibling;

    // Sentinel marks where to move the container back to on close.
    STATE.sentinel = document.createComment('customily-original-slot');
    STATE.originalParent.insertBefore(STATE.sentinel, container);

    STATE.section = container.closest('.shopify-section') || document.body;
    const sectionId = (STATE.section.id || 'main-product').replace(/^shopify-section-/, '');

    // Hide existing locket Start Customize button (Customily wins on this product)
    STATE.locketBtn = STATE.section.querySelector('.locket-start-customize-btn');
    if (STATE.locketBtn) STATE.locketBtn.style.display = 'none';

    // Hide Customily's injected container in place (we'll show it inside the modal)
    container.dataset.customilyBridgeManaged = '1';
    container.style.display = 'none';

    // Find ATC buttons and hide them until customization is done
    const productForm = STATE.section.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      STATE.atcButtons = Array.from(
        productForm.querySelectorAll('.product-form__submit, button[type="submit"][name="add"], button[name="add"]')
      );
      STATE.atcButtons.forEach((btn) => { btn.style.display = 'none'; });
    }

    // Build & inject trigger button
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'customily-start-customize-btn product-form__submit button button--full-width button--primary';
    trigger.setAttribute('data-section-id', sectionId);
    trigger.innerHTML = '<span>Start Customizing</span>';
    STATE.triggerBtn = trigger;

    // Insert trigger near where the locket button was, or before the ATC button, or before the container
    const insertAnchor = STATE.locketBtn || STATE.atcButtons[0] || container;
    if (insertAnchor && insertAnchor.parentNode) {
      insertAnchor.parentNode.insertBefore(trigger, insertAnchor);
    } else if (productForm) {
      productForm.appendChild(trigger);
    } else {
      container.parentNode.insertBefore(trigger, container);
    }

    // Modal
    STATE.modal = buildModal(sectionId);
    STATE.modalBody = STATE.modal.querySelector('.customily-modal__body');

    // Wire events
    trigger.addEventListener('click', openModal);
    STATE.modal.querySelector('.customily-modal__close').addEventListener('click', closeModal);
    STATE.modal.querySelector('.customily-modal__backdrop').addEventListener('click', closeModal);
    STATE.modal.querySelector('.customily-modal__done').addEventListener('click', completeAndClose);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && STATE.modal && STATE.modal.classList.contains('active')) {
        closeModal();
      }
    });

    return true;
  }

  function openModal() {
    if (!STATE.modal || !STATE.container) return;
    STATE.container.style.display = '';
    STATE.modalBody.appendChild(STATE.container);
    STATE.modal.classList.add('active');
    STATE.modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!STATE.modal || !STATE.container) return;
    // Move container back to its original slot (preserves Customily's listeners)
    if (STATE.sentinel && STATE.sentinel.parentNode) {
      STATE.sentinel.parentNode.insertBefore(STATE.container, STATE.sentinel);
    } else if (STATE.originalParent) {
      STATE.originalParent.appendChild(STATE.container);
    }
    STATE.container.style.display = 'none';
    STATE.modal.classList.remove('active');
    STATE.modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  function completeAndClose() {
    closeModal();
    // Reveal ATC so the customer can add to cart with the Customily properties already set
    STATE.atcButtons.forEach((btn) => { btn.style.display = ''; });
    if (STATE.triggerBtn) {
      // Swap trigger for a re-edit affordance
      STATE.triggerBtn.querySelector('span').textContent = 'Edit Customization';
    }
  }

  // Polling: Customily can inject at any point after page load.
  let attempts = 0;
  function poll() {
    if (init()) return;
    attempts++;
    if (attempts < 60) {
      // 60 * 250ms = 15s of polling
      setTimeout(poll, 250);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', poll);
  } else {
    poll();
  }

  // Also observe DOM mutations in case Customily injects very late
  try {
    const obs = new MutationObserver(function () {
      if (!STATE.container) init();
    });
    obs.observe(document.body, { childList: true, subtree: true });
    // Disconnect after 30s to avoid wasting cycles
    setTimeout(function () { obs.disconnect(); }, 30000);
  } catch (e) {
    // MutationObserver not supported — polling is enough.
  }
})();
